/**
 * Rechnung aus Aufmaß — erzeugt aus den (offenen) Aufmaß-Mengen der
 * aktuellen LV-Positionen einen Rechnungsentwurf über
 * POST /api/quotes/to-invoice.
 *
 * Die Route erfordert einen SSO-Login (requireSsoAuth); bei 401/403 wird
 * ein entsprechender Hinweis angezeigt. Wird vom Orchestrator eingebunden,
 * nicht direkt in der Sidebar.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FileDown, Check, Loader2 } from 'lucide-react';
import { useOfferStore } from '@/lib/store';
import { useToast } from '@/hooks/use-toast';
import { buildInvoicePositionsFromAufmass } from '@shared/aufmass-to-invoice';

interface CreatedInvoice {
  id?: string;
  invoiceNumber?: string | null;
  draftNumber?: string | null;
}

export default function AufmassRechnungButton() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [created, setCreated] = useState<CreatedInvoice | null>(null);

  const quoteHeader = useOfferStore((state) => state.quoteHeader);
  const quoteItems = useOfferStore((state) => state.quoteItems);

  const positions = useMemo(
    () => buildInvoicePositionsFromAufmass(quoteItems, { nurOffene: true }),
    [quoteItems],
  );

  const handleCreate = async () => {
    if (positions.length === 0) {
      toast({
        title: 'Keine offenen Aufmaß-Mengen',
        description: 'Es gibt keine noch nicht abgerechneten Aufmaß-Mengen für eine Rechnung.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    setCreated(null);

    try {
      const response = await fetch('/api/quotes/to-invoice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          positions,
          clientName: quoteHeader.kunde || undefined,
          clientAddress: quoteHeader.anschrift || undefined,
          projectName: quoteHeader.bauvorhaben || undefined,
          // Firmen-Briefkopf-Profil des Angebots 1:1 in die Rechnung übernehmen
          // (Codex Runde 8, Finding 1) — sonst friert die Finalisierung das
          // aktuell aktive Profil statt des Angebots-Profils ein.
          companyProfileId: quoteHeader.companyProfileId || undefined,
        }),
      });

      if (response.status === 401 || response.status === 403) {
        toast({
          title: 'Anmeldung erforderlich',
          description: 'Rechnung aus Aufmaß ist nur mit SSO-Login (über die Hauptanwendung) verfügbar.',
          variant: 'destructive',
        });
        return;
      }

      const payload = await response.json().catch(() => null);

      if (!response.ok || !payload?.success) {
        const message = payload?.error || `Server-Fehler ${response.status}`;
        throw new Error(message);
      }

      // API liefert { success, data: { invoice, items, payments } } (quote-to-invoice.ts);
      // ältere Antworten trugen die Rechnung direkt in data.
      const invoice = ((payload.data?.invoice ?? payload.data) ?? {}) as CreatedInvoice;
      setCreated(invoice);
      const kennung = invoice.invoiceNumber || invoice.draftNumber || invoice.id || 'Entwurf';
      toast({
        title: 'Rechnungsentwurf erstellt',
        description: `Rechnung ${kennung} aus ${positions.length} Aufmaß-Position(en) angelegt.`,
        variant: 'success',
      });

      // Positionen ohne erfassten Preis (preisFehlt) landen mit 0,00 € im Entwurf —
      // das darf NICHT still passieren: laut warnen, damit der Preis vor der
      // Finalisierung nachgetragen wird.
      const missingPriceCount = positions.filter((p) => p.priceMissing).length;
      if (missingPriceCount > 0) {
        toast({
          title: `${missingPriceCount} Position(en) ohne Preis`,
          description:
            'Diese LV-Positionen wurden ohne Preise importiert und stehen im Rechnungsentwurf mit 0,00 €. Preise vor der Finalisierung nachtragen!',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Rechnung aus Aufmaß fehlgeschlagen:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      toast({
        title: 'Rechnung fehlgeschlagen',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-1">
      <Button
        variant="outline"
        onClick={handleCreate}
        disabled={isSubmitting || positions.length === 0}
        className="w-full text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
        data-testid="button-aufmass-rechnung"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Rechnung wird erstellt...
          </>
        ) : created ? (
          <>
            <Check className="h-3 w-3 mr-1" />
            Rechnung erstellt
          </>
        ) : (
          <>
            <FileDown className="h-3 w-3 mr-1" />
            Rechnung aus Aufmaß
          </>
        )}
      </Button>
      {created && (
        <p className="text-[11px] text-gray-500 px-1" data-testid="text-aufmass-rechnung-result">
          Entwurf {created.invoiceNumber || created.draftNumber || created.id} angelegt. In der
          Hauptanwendung unter „Rechnungen" bearbeiten.
        </p>
      )}
    </div>
  );
}
